/**
 * Maintenance — /maintenance page (Feature 4, slice 4b)
 * Two tabs: Work (agenda, exception-first) and Schedules (standing plans CRUD).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Chip, Button, IconButton,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  CircularProgress, Collapse, Checkbox, FormControlLabel, Divider,
} from '@mui/material';
import { RefreshCw, ChevronDown, ChevronRight, Calendar, Pause, Play, Square, History, Plus, X, Pencil } from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useSearchParams, useNavigate } from 'react-router-dom';
import BASE_URL from '../config';
import { useOrg } from '../contexts/OrgContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useAuthData } from '../contexts/AuthDataContext';
import UnifiedTaskModal from '../Components/Task/UnifiedTaskModal';
import StageFieldEditor from '../Components/Workflow/StageFieldEditor';
import useAssignableUsers, { filterVendorCrewByProvider, renderAssignableMenuItems } from '../hooks/useAssignableUsers';

const getToken = async () => {
  const user = getAuth().currentUser;
  return user ? await user.getIdToken() : null;
};

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

// Normalizes a timestamp to local midnight on a copy — used to compare/bucket
// due_date values by calendar day without comparing raw getTime() values.
const startOfDay = (d) => { const c = new Date(d); c.setHours(0, 0, 0, 0); return c; };

// Status dot color for a visit on a given day (palette: red/amber/grey/green).
const dotColorFor = (v, todayStart) => {
  if (v.projected) return '#bdbdbd';
  if (v.outcome === 'on_time') return '#6BA368';
  if (v.outcome === 'missed') return '#D2726A';
  if (v.outcome === 'delayed') return '#E0A458';
  if (!v.due_date) return '#9e9e9e';
  const vStart = startOfDay(v.due_date);
  if (vStart.toDateString() === todayStart.toDateString()) return '#E0A458';
  if (vStart < todayStart && v.status === 'active') return '#D2726A';
  return '#9e9e9e';
};

// Parses the "[site:<id>|type:tree|grid:<id>]" marker suggested-inspection notifications
// embed in their message (see maintenanceScheduler.js / sentinelController.js).
const suggestionLink = (message) => {
  const m = message.match(/\[site:([^|]+)\|type:([^|\]]+)(?:\|grid:([^\]]+))?\]/);
  if (!m) return '/ecological';
  const [, siteId, type, gridId] = m;
  const page = type === 'lake' ? '/Lake' : '/ecological';
  return `${page}?site=${siteId}${gridId ? `&grid=${gridId}` : ''}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Day side panel — card shown to the right of a calendar when a day is clicked,
// listing that day's visits (real + projected). Shared by MonthStrip and
// MonthCalendar; the calendar shrinks (flex) to make room for it.
// ─────────────────────────────────────────────────────────────────────────────
const DayVisitsPanel = ({ date, visits, onClose, staff, vendorCrew, onOpen, onAssign, onReschedule, onViewPlan }) => (
  <Paper variant="outlined" sx={{ width: 320, flexShrink: 0, p: 2, alignSelf: 'flex-start' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Typography variant="subtitle1">{fmtDate(date)}</Typography>
      <IconButton size="small" onClick={onClose}><X size={16} /></IconButton>
    </Box>
    {visits.length === 0 && (
      <Typography variant="body2" color="text.secondary">No visits due this day.</Typography>
    )}
    {visits.map(v => (
      <Box key={v.id || `proj-${v.plan_id}-${v.due_date}`} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        {v.projected ? (
          <>
            <Typography variant="body2" fontWeight={500}>{v.plan_name} — planned</Typography>
            <Typography variant="caption" color="text.secondary">
              {v.site_name} {v.assignee_name ? `· ${v.assignee_name}` : '· unassigned'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Button size="small" onClick={() => { onViewPlan?.(); onClose(); }}>View plan</Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" fontWeight={500}>{v.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {v.site_name} {v.assignee_name ? `· ${v.assignee_name}` : '· unassigned'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Button size="small" onClick={() => { onOpen(v.id); onClose(); }}>Open</Button>
              {!v.assigned_to && (
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <Select displayEmpty value="" onChange={e => { onAssign(v.id, e.target.value); onClose(); }}>
                    <MenuItem value="" disabled>Assign…</MenuItem>
                    {renderAssignableMenuItems(staff, filterVendorCrewByProvider(vendorCrew, v.service_provider_id || v.contract_service_provider_id))}
                  </Select>
                </FormControl>
              )}
              <Button size="small" onClick={() => { onReschedule(v); onClose(); }}>Reschedule</Button>
            </Box>
          </>
        )}
      </Box>
    ))}
  </Paper>
);

// Slides DayVisitsPanel in/out horizontally. Retains the last non-null date's
// content so the card doesn't go blank during the exit animation.
const SlidingDayPanel = ({ date, visits, ...rest }) => {
  const lastRef = useRef({ date, visits });
  if (date) lastRef.current = { date, visits };
  const shown = lastRef.current;
  return (
    <Collapse in={!!date} orientation="horizontal" mountOnEnter unmountOnExit sx={{ alignSelf: 'flex-start' }}>
      <DayVisitsPanel date={shown.date} visits={shown.visits} {...rest} />
    </Collapse>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// V6a — Full month calendar + day drawer
// ─────────────────────────────────────────────────────────────────────────────
const outcomeIcon = (v) => {
  if (v.projected) return '○';
  if (v.outcome === 'missed') return '❌';
  if (v.outcome === 'delayed') return '⏱';
  if (v.outcome === 'on_time') return '✅';
  if (v.status === 'active' && v.due_date && new Date(v.due_date) < new Date(new Date().toDateString())) return '❌';
  if (v.due_date && new Date(v.due_date).toDateString() === new Date().toDateString()) return '▶';
  return '○';
};

const MonthCalendar = ({ organisationId, siteId, onOpen, onReschedule, staff, vendorCrew, onAssign, onViewPlan }) => {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerDate, setDrawerDate] = useState(null);

  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const params = { organisation_id: organisationId, month };
        if (siteId) params.site_id = siteId;
        const res = await axios.get(`${BASE_URL}/api/maintenance-calendar`, {
          params, headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setVisits(res.data?.visits || []);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [organisationId, month, siteId]);

  const byDay = {};
  visits.forEach(v => {
    if (!v.due_date) return;
    const day = v.due_date.slice(0, 10);
    (byDay[day] = byDay[day] || []).push(v);
  });

  const [year, monthNum] = month.split('-').map(Number);
  const firstOfMonth = new Date(year, monthNum - 1, 1);
  const daysInMonth = new Date(year, monthNum, 0).getDate();
  const startWeekday = firstOfMonth.getDay();
  const cells = [...Array(startWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const shiftMonth = (delta) => {
    const d = new Date(year, monthNum - 1 + delta, 1);
    setMonth(d.toISOString().slice(0, 7));
  };

  const drawerVisits = drawerDate ? (byDay[drawerDate] || []) : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <IconButton size="small" onClick={() => shiftMonth(-1)}><ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} /></IconButton>
        <Typography variant="subtitle1" sx={{ minWidth: 140, textAlign: 'center' }}>
          {firstOfMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
        </Typography>
        <IconButton size="small" onClick={() => shiftMonth(1)}><ChevronRight size={16} /></IconButton>
        {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          ○ planned (not yet created) · ▶ due today · ✅ on time · ⏱ delayed · ❌ missed
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {WEEKDAY_LABELS.map(d => (
              <Typography key={d} variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 600 }}>{d}</Typography>
            ))}
            {cells.map((day, idx) => {
              if (!day) return <Box key={`empty-${idx}`} />;
              const dateStr = `${year}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayVisits = byDay[dateStr] || [];
              const isToday = dateStr === new Date().toISOString().slice(0, 10);
              return (
                <Paper
                  key={dateStr}
                  variant="outlined"
                  onClick={() => dayVisits.length && setDrawerDate(prev => prev === dateStr ? null : dateStr)}
                  sx={{
                    p: 0.75, minHeight: 60, cursor: dayVisits.length ? 'pointer' : 'default',
                    borderColor: isToday ? 'primary.main' : 'divider',
                    borderWidth: isToday ? 2 : 1,
                    '&:hover': dayVisits.length ? { bgcolor: 'action.hover' } : {},
                  }}
                >
                  <Typography variant="caption" color="text.secondary">{day}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 0.5 }}>
                    {dayVisits.slice(0, 4).map(v => (
                      <Typography key={v.id || `proj-${v.plan_id}-${v.due_date}`} variant="caption" title={v.projected ? `${v.plan_name} — planned` : v.title}>{outcomeIcon(v)}</Typography>
                    ))}
                    {dayVisits.length > 4 && <Typography variant="caption" color="text.secondary">+{dayVisits.length - 4}</Typography>}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>

        <SlidingDayPanel
          date={drawerDate}
          visits={drawerVisits}
          onClose={() => setDrawerDate(null)}
          staff={staff}
          vendorCrew={vendorCrew}
          onOpen={onOpen}
          onAssign={onAssign}
          onReschedule={onReschedule}
          onViewPlan={onViewPlan}
        />
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Month strip — compact header above the agenda list (replaces the old
// List/Calendar toggle). Click a day to filter the agenda; expand to show
// the full MonthCalendar in place of the agenda list.
// ─────────────────────────────────────────────────────────────────────────────
const MonthStrip = ({ organisationId, siteId, selectedDay, onSelectDay, expanded, onToggleExpand, staff, vendorCrew, onOpen, onAssign, onReschedule, onViewPlan }) => {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const params = { organisation_id: organisationId, month };
        if (siteId) params.site_id = siteId;
        const res = await axios.get(`${BASE_URL}/api/maintenance-calendar`, {
          params, headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setVisits(res.data?.visits || []);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [organisationId, month, siteId]);

  const todayStart = startOfDay(new Date());
  const byDay = {};
  visits.forEach(v => {
    if (!v.due_date) return;
    const key = startOfDay(v.due_date).toDateString();
    (byDay[key] = byDay[key] || []).push(v);
  });

  const [year, monthNum] = month.split('-').map(Number);
  const firstOfMonth = new Date(year, monthNum - 1, 1);
  const daysInMonth = new Date(year, monthNum, 0).getDate();
  const startWeekday = firstOfMonth.getDay();
  const cells = [...Array(startWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const shiftMonth = (delta) => {
    const d = new Date(year, monthNum - 1 + delta, 1);
    setMonth(d.toISOString().slice(0, 7));
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton size="small" onClick={() => shiftMonth(-1)}><ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /></IconButton>
        <Typography variant="subtitle2" sx={{ minWidth: 120, textAlign: 'center' }}>
          {firstOfMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
        </Typography>
        <IconButton size="small" onClick={() => shiftMonth(1)}><ChevronRight size={14} /></IconButton>
        {loading && <CircularProgress size={14} sx={{ ml: 1 }} />}
        <IconButton size="small" sx={{ ml: 'auto' }} onClick={onToggleExpand} title={expanded ? 'Collapse calendar' : 'Expand calendar'}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} style={{ transform: 'rotate(-90deg)' }} />}
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, alignSelf: 'flex-start' }}>
          {WEEKDAY_LABELS.map(d => (
            <Typography key={d} variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 600 }}>{d}</Typography>
          ))}
          {cells.map((day, idx) => {
            if (!day) return <Box key={`empty-${idx}`} />;
            const cellDate = new Date(year, monthNum - 1, day);
            const key = cellDate.toDateString();
            const dayVisits = byDay[key] || [];
            const isToday = key === todayStart.toDateString();
            const isSelected = selectedDay === key;
            return (
              <Box
                key={key}
                onClick={() => onSelectDay(isSelected ? null : key)}
                sx={{
                  p: 0.5, minHeight: 36, textAlign: 'center', cursor: 'pointer', borderRadius: 1,
                  border: isToday ? '2px solid' : '1px solid',
                  borderColor: isToday ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="caption">{day}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25, mt: 0.25 }}>
                  {dayVisits.slice(0, 3).map(v => (
                    <Box key={v.id || `proj-${v.plan_id}-${v.due_date}`} sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: dotColorFor(v, todayStart) }} />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>

        <SlidingDayPanel
          date={selectedDay}
          visits={byDay[selectedDay] || []}
          onClose={() => onSelectDay(null)}
          staff={staff}
          vendorCrew={vendorCrew}
          onOpen={onOpen}
          onAssign={onAssign}
          onReschedule={onReschedule}
          onViewPlan={onViewPlan}
        />
      </Box>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Work tab (agenda)
// ─────────────────────────────────────────────────────────────────────────────
const GROUP_META = {
  overdue: { label: 'Overdue', color: 'error', defaultOpen: true },
  unassigned: { label: 'Unassigned', color: 'warning', defaultOpen: true },
  today: { label: 'Today', color: 'info', defaultOpen: true },
  this_week: { label: 'This week', color: 'default', defaultOpen: false },
  upcoming: { label: 'Upcoming', color: 'default', defaultOpen: false },
  scheduled: { label: 'Scheduled (upcoming)', color: 'default', defaultOpen: false },
};

const OUTCOME_LABELS = { on_time: 'on time', delayed: 'delayed', missed: 'missed' };

const recurrenceLabel = (rec) => {
  if (!rec?.frequency) return null;
  const n = rec.interval || 1;
  const unit = rec.frequency === 'daily' ? 'day' : rec.frequency === 'weekly' ? 'week' : 'month';
  return n === 1 ? `every ${unit}` : `every ${n} ${unit}s`;
};

const VisitCard = ({ visit, onOpen, onReschedule, staff, vendorCrew, onAssign }) => {
  const cadence = recurrenceLabel(visit.plan_recurrence);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.25,
      borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' },
    }}>
      <Box sx={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={() => onOpen(visit.id)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{visit.title}</Typography>
          {visit.outcome === 'missed' && <Chip label="Missed" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {visit.plan_name || 'Maintenance'}
          {cadence ? ` · ${cadence}` : ''}
          {' · '}{visit.site_name || visit.site_id}
          {visit.grid_id ? ` · Grid ${visit.grid_id}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }} color="text.secondary">
          Due {fmtDate(visit.due_date)} ·{' '}
          {visit.assignee_name?.trim()
            ? <>Assigned to <b>{visit.assignee_name}</b></>
            : <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>Unassigned</Box>}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {visit.last_done_at
            ? `Last done ${fmtDate(visit.last_done_at)}${visit.last_done_by?.trim() ? ` by ${visit.last_done_by}` : ''}${visit.last_outcome ? ` (${OUTCOME_LABELS[visit.last_outcome] || visit.last_outcome})` : ''}`
            : 'No previous visit completed yet'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
        {!visit.assigned_to && (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select displayEmpty value="" onChange={e => onAssign(visit.id, e.target.value)}>
              <MenuItem value="" disabled>Assign…</MenuItem>
              {renderAssignableMenuItems(staff, filterVendorCrewByProvider(vendorCrew, visit.service_provider_id || visit.contract_service_provider_id))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" onClick={() => onOpen(visit.id)}>Open</Button>
          <Button size="small" onClick={() => onReschedule(visit)}>Reschedule</Button>
        </Box>
      </Box>
    </Box>
  );
};

// Read-only card for a projected (not-yet-spawned) visit — no task id exists yet,
// so there's nothing to open/assign/reschedule; the only action is jumping to the plan.
const ScheduledCard = ({ visit, onViewPlan }) => {
  const cadence = recurrenceLabel(visit.plan_recurrence);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.25,
      borderBottom: '1px solid', borderColor: 'divider',
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>{visit.plan_name} — {fmtDate(visit.due_date)}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {visit.site_name || visit.site_id}
          {cadence ? ` · ${cadence}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }} color="text.secondary">
          {visit.assignee_name?.trim()
            ? <>Will be assigned to <b>{visit.assignee_name}</b></>
            : <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>Unassigned — set a default assignee on the plan</Box>}
        </Typography>
      </Box>
      <Button size="small" onClick={onViewPlan}>View plan</Button>
    </Box>
  );
};

const WorkTab = ({ organisationId, siteId, onViewPlan }) => {
  const navigate = useNavigate();
  const { userData } = useAuthData();
  const { can } = usePermissions();
  const [agenda, setAgenda] = useState({ groups: {}, workload: {} });
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState(() =>
    Object.fromEntries(Object.entries(GROUP_META).map(([k, v]) => [k, v.defaultOpen])));
  const [assigneeMineOnly, setAssigneeMineOnly] = useState(false);
  const hasUserToggledMineOnlyRef = useRef(false);
  const { staff, vendorCrew } = useAssignableUsers(organisationId);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [suggestions, setSuggestions] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null); // toDateString() key, or null
  const [expanded, setExpanded] = useState(false);

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  // Field workers (no task-create permission) default to "Assigned to me";
  // managers default to seeing all. Runs once, and never overrides a manual toggle.
  useEffect(() => {
    if (hasUserToggledMineOnlyRef.current) return;
    setAssigneeMineOnly(!can('Task Management', 'taskAssignment', 'create'));
  }, [can]);

  const handleMineOnlyChange = (checked) => {
    hasUserToggledMineOnlyRef.current = true;
    setAssigneeMineOnly(checked);
  };

  const fetchSuggestions = useCallback(async () => {
    if (!organisationId) return;
    try {
      const token = await getToken();
      const res = await axios.get(`${BASE_URL}/api/notifications`, {
        params: { organisation_id: organisationId, type: 'suggested_inspection', limit: 20 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuggestions((res.data?.notifications || []).filter(n => !n.is_read));
    } catch (err) {
      console.error(err);
    }
  }, [organisationId]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const dismissSuggestion = async (id) => {
    try {
      const token = await getToken();
      await axios.put(`${BASE_URL}/api/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSuggestions(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      showSnack('Failed to dismiss', 'error');
    }
  };

  const fetchAgenda = useCallback(async () => {
    if (!organisationId) return;
    setLoading(true);
    try {
      const token = await getToken();
      const params = { organisation_id: organisationId };
      if (assigneeMineOnly && userData?.user_id) params.assigned_to = userData.user_id;
      if (siteId) params.site_id = siteId;
      const res = await axios.get(`${BASE_URL}/api/maintenance-agenda`, {
        params, headers: { Authorization: `Bearer ${token}` },
      });
      setAgenda(res.data || { groups: {}, workload: {} });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [organisationId, assigneeMineOnly, userData?.user_id, siteId]);

  useEffect(() => { fetchAgenda(); }, [fetchAgenda]);

  const handleAssign = async (taskId, userId) => {
    try {
      const token = await getToken();
      await axios.put(`${BASE_URL}/api/tasks/${taskId}?organisation_id=${organisationId}`,
        { assigned_to: userId },
        { headers: { Authorization: `Bearer ${token}` } });
      showSnack('Assigned');
      fetchAgenda();
    } catch (err) {
      showSnack(err.response?.data?.error || 'Failed to assign', 'error');
    }
  };

  const submitReschedule = async () => {
    if (!rescheduleTarget || !rescheduleDate) return;
    try {
      const token = await getToken();
      await axios.put(`${BASE_URL}/api/tasks/${rescheduleTarget.id}?organisation_id=${organisationId}`,
        { due_date: rescheduleDate },
        { headers: { Authorization: `Bearer ${token}` } });
      showSnack('Visit rescheduled');
      setRescheduleTarget(null);
      fetchAgenda();
    } catch (err) {
      showSnack(err.response?.data?.error || 'Failed to reschedule', 'error');
    }
  };

  const workloadEntries = Object.entries(agenda.workload || {}).sort((a, b) => b[1] - a[1]);
  const maxWorkload = Math.max(1, ...workloadEntries.map(([, count]) => count));

  const filterBySelectedDay = (rows) => {
    if (!selectedDay) return rows;
    return rows.filter(v => v.due_date && startOfDay(v.due_date).toDateString() === selectedDay);
  };
  // "Scheduled" is informational (not-yet-created visits) and shouldn't affect
  // the "Nothing needs attention" empty state, which is about actionable groups.
  const filteredTotalCount = Object.entries(agenda.groups || {})
    .filter(([key]) => key !== 'scheduled')
    .reduce((sum, [, g]) => sum + filterBySelectedDay(g).length, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={assigneeMineOnly} onChange={e => handleMineOnlyChange(e.target.checked)} size="small" />}
          label={<Typography variant="body2">Assigned to me</Typography>}
        />
        <IconButton size="small" onClick={fetchAgenda}><RefreshCw size={16} /></IconButton>
        {loading && <CircularProgress size={16} />}
      </Box>

      <MonthStrip
        organisationId={organisationId}
        siteId={siteId}
        selectedDay={selectedDay}
        onSelectDay={setSelectedDay}
        expanded={expanded}
        onToggleExpand={() => setExpanded(e => !e)}
        staff={staff}
        vendorCrew={vendorCrew}
        onOpen={setSelectedTaskId}
        onAssign={handleAssign}
        onReschedule={(visit) => { setRescheduleTarget(visit); setRescheduleDate(visit.due_date?.slice(0, 10) || ''); }}
        onViewPlan={onViewPlan}
      />

      {selectedDay && !expanded && (
        <Chip
          label={`Showing ${fmtDateShort(selectedDay)} ✕`}
          onDelete={() => setSelectedDay(null)}
          size="small"
          sx={{ mb: 1.5 }}
        />
      )}

      {suggestions.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Suggested inspections <Chip label={suggestions.length} size="small" color="info" />
          </Typography>
          <Paper variant="outlined">
            {suggestions.map(s => (
              <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ flex: 1 }}>{s.message.replace(/\s*\[site:[^\]]*\]\s*$/, '')}</Typography>
                <Button size="small" onClick={() => navigate(suggestionLink(s.message))}>Approve</Button>
                <Button size="small" color="inherit" onClick={() => dismissSuggestion(s.id)}>Dismiss</Button>
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      {expanded ? (
        <MonthCalendar
          organisationId={organisationId}
          siteId={siteId}
          staff={staff}
          vendorCrew={vendorCrew}
          onOpen={setSelectedTaskId}
          onAssign={handleAssign}
          onReschedule={(visit) => { setRescheduleTarget(visit); setRescheduleDate(visit.due_date?.slice(0, 10) || ''); }}
          onViewPlan={onViewPlan}
        />
      ) : (
        <>
          {workloadEntries.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                This week by assignee
              </Typography>
              {workloadEntries.map(([name, count]) => (
                <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" noWrap sx={{ width: 120, flexShrink: 0 }}>{name}</Typography>
                  <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
                    <Box sx={{ width: `${(count / maxWorkload) * 100}%`, height: '100%', borderRadius: 1, bgcolor: 'primary.main' }} />
                  </Box>
                  <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'right' }}>{count}</Typography>
                </Box>
              ))}
            </Paper>
          )}

          {!loading && filteredTotalCount === 0 && (
            <Alert severity="success">
              {selectedDay ? 'No visits due that day.' : 'Nothing needs attention — all maintenance visits are on track.'}
            </Alert>
          )}

          {Object.entries(GROUP_META).map(([key, meta]) => {
            const rows = filterBySelectedDay(agenda.groups?.[key] || []);
            if (!rows.length) return null;
            const open = openGroups[key];
            return (
              <Box key={key} sx={{ mb: 1.5 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', py: 0.5 }}
                  onClick={() => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }))}
                >
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <Typography variant="subtitle2">{meta.label}</Typography>
                  <Chip label={rows.length} size="small" color={meta.color} />
                </Box>
                <Collapse in={open}>
                  <Paper variant="outlined">
                    {key === 'scheduled'
                      ? rows.map(v => (
                          <ScheduledCard key={`proj-${v.plan_id}-${v.due_date}`} visit={v} onViewPlan={onViewPlan} />
                        ))
                      : rows.map(v => (
                          <VisitCard
                            key={v.id}
                            visit={v}
                            staff={staff}
                            vendorCrew={vendorCrew}
                            onOpen={setSelectedTaskId}
                            onAssign={handleAssign}
                            onReschedule={(visit) => { setRescheduleTarget(visit); setRescheduleDate(visit.due_date?.slice(0, 10) || ''); }}
                          />
                        ))}
                  </Paper>
                </Collapse>
              </Box>
            );
          })}
        </>
      )}

      <UnifiedTaskModal
        open={!!selectedTaskId}
        taskId={selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
        onUpdated={fetchAgenda}
      />

      <Dialog open={!!rescheduleTarget} onClose={() => setRescheduleTarget(null)}>
        <DialogTitle>Reschedule visit</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            type="date" label="New due date" fullWidth size="small"
            value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitReschedule}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// New plan dialog
// ─────────────────────────────────────────────────────────────────────────────
const NewPlanDialog = ({ open, onClose, organisationId, onCreated }) => {
  const [sites, setSites] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [name, setName] = useState('');
  const [siteId, setSiteId] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [interval, setIntervalVal] = useState(1);
  const [weekdays, setWeekdays] = useState([]);
  const [leadDays, setLeadDays] = useState(3);
  const [until, setUntil] = useState('');
  const [contractId, setContractId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [activities, setActivities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // contractdropdown rows don't carry service_provider_id, so scope the
  // vendor-crew group server-side via the assignable-users contract_id filter.
  const { staff, vendorCrew } = useAssignableUsers(open ? organisationId : null, contractId || undefined);

  useEffect(() => {
    if (!open || !organisationId) return;
    (async () => {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [s, c] = await Promise.all([
        axios.get(`${BASE_URL}/api/sites`, { params: { organisation_id: organisationId }, headers }),
        axios.get(`${BASE_URL}/api/contractdropdown`, { params: { organisation_id: organisationId }, headers }),
      ]);
      setSites(s.data || []);
      setContracts(c.data || []);
    })().catch(console.error);
  }, [open, organisationId]);

  const toggleWeekday = (d) => setWeekdays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);

  const submit = async () => {
    if (!name.trim() || !siteId || activities.length === 0) {
      setError('Name, site, and at least one activity are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = await getToken();
      await axios.post(`${BASE_URL}/api/maintenance-plans`, {
        organisation_id: organisationId,
        site_id: siteId,
        name,
        recurrence: {
          frequency, interval: Number(interval) || 1,
          weekdays: frequency === 'weekly' && weekdays.length ? weekdays : undefined,
          lead_days: Number(leadDays) || 0,
          until: until || undefined,
        },
        activities: activities.map((f, i) => ({
          field_key: f.field_key, label: f.label, field_type: f.field_type,
          options: f.options?.length ? f.options : null, is_required: f.is_required, position: i + 1,
        })),
        contract_id: contractId || null,
        assigned_to: assignedTo || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New maintenance plan</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Plan name" value={name} onChange={e => setName(e.target.value)} size="small" fullWidth required />
        <FormControl size="small" fullWidth>
          <InputLabel>Site</InputLabel>
          <Select value={siteId} label="Site" onChange={e => setSiteId(e.target.value)}>
            {sites.map(s => <MenuItem key={s.site_id} value={s.site_id}>{s.name}</MenuItem>)}
          </Select>
        </FormControl>

        <Divider><Typography variant="caption">Recurrence</Typography></Divider>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Frequency</InputLabel>
            <Select value={frequency} label="Frequency" onChange={e => setFrequency(e.target.value)}>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Every N" type="number" size="small" sx={{ width: 100 }}
            value={interval} onChange={e => setIntervalVal(e.target.value)} inputProps={{ min: 1 }} />
          <TextField label="Lead days" type="number" size="small" sx={{ width: 110 }}
            value={leadDays} onChange={e => setLeadDays(e.target.value)} inputProps={{ min: 0 }} />
        </Box>
        {frequency === 'weekly' && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {WEEKDAY_LABELS.map((label, idx) => (
              <Chip key={idx} label={label} size="small"
                color={weekdays.includes(idx) ? 'primary' : 'default'}
                onClick={() => toggleWeekday(idx)} />
            ))}
          </Box>
        )}
        <TextField label="Until (optional)" type="date" size="small" value={until}
          onChange={e => setUntil(e.target.value)} InputLabelProps={{ shrink: true }} />

        <Divider><Typography variant="caption">Assignment</Typography></Divider>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Contract (optional)</InputLabel>
            <Select value={contractId} label="Contract (optional)" onChange={e => setContractId(e.target.value)}>
              <MenuItem value="">None</MenuItem>
              {contracts.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Default assignee</InputLabel>
            <Select value={assignedTo} label="Default assignee" onChange={e => setAssignedTo(e.target.value)}>
              <MenuItem value="">Unassigned</MenuItem>
              {renderAssignableMenuItems(staff, vendorCrew)}
            </Select>
          </FormControl>
        </Box>

        <Divider><Typography variant="caption">Visit checklist</Typography></Divider>
        <StageFieldEditor fields={activities} onChange={setActivities} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? <CircularProgress size={16} /> : 'Create plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Plan history dialog (V6b, folded in as a dialog rather than a separate view)
// ─────────────────────────────────────────────────────────────────────────────
const PlanHistoryDialog = ({ planId, onClose }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!planId) return;
    (async () => {
      const token = await getToken();
      const res = await axios.get(`${BASE_URL}/api/maintenance-plans/${planId}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    })().catch(console.error);
  }, [planId]);

  return (
    <Dialog open={!!planId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.plan?.name || 'Plan history'}</DialogTitle>
      <DialogContent>
        {!data ? <CircularProgress size={20} /> : (() => {
          const closedVisits = (data.visits || []).filter(v => v.outcome);
          const sortedClosed = [...closedVisits].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
          const trend = sortedClosed.slice(-12);
          let streak = 0;
          for (let i = sortedClosed.length - 1; i >= 0; i--) {
            if (sortedClosed[i].outcome === 'on_time') streak++;
            else break;
          }
          return (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Compliance: {data.compliance.compliance_pct != null ? `${data.compliance.compliance_pct}%` : '—'}
              {' · '}✅ {data.compliance.on_time} ⏱ {data.compliance.delayed} ❌ {data.compliance.missed}
            </Typography>
            {trend.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Recent trend:</Typography>
                {trend.map(v => (
                  <Box
                    key={v.id}
                    title={`${fmtDate(v.due_date)}: ${v.outcome}`}
                    sx={{
                      width: 10, height: 10, borderRadius: 0.5,
                      bgcolor: v.outcome === 'on_time' ? '#6BA368' : v.outcome === 'delayed' ? '#E0A458' : '#D2726A',
                    }}
                  />
                ))}
                <Typography variant="caption" sx={{ ml: 1 }}>Current streak: {streak} ✅</Typography>
              </Box>
            )}
            {data.visits.map(v => (
              <Box key={v.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2">{v.title}</Typography>
                <Typography variant="body2" color="text.secondary">{fmtDate(v.due_date)}</Typography>
                <Chip
                  size="small"
                  label={v.outcome ? (v.outcome === 'on_time' ? 'On time' : v.outcome.charAt(0).toUpperCase() + v.outcome.slice(1)) : v.status}
                  color={v.outcome === 'on_time' ? 'success' : v.outcome === 'delayed' ? 'warning' : v.outcome === 'missed' ? 'error' : 'default'}
                />
              </Box>
            ))}
          </>
          );
        })()}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Edit plan dialog — contract + assigned-to only (name/recurrence/activities
// are not editable here)
// ─────────────────────────────────────────────────────────────────────────────
const EditPlanDialog = ({ plan, onClose, organisationId, onSaved, onError }) => {
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { staff, vendorCrew } = useAssignableUsers(plan ? organisationId : null, contractId || undefined);

  useEffect(() => {
    if (!plan) return;
    setContractId(plan.contract_id || '');
    setAssignedTo(plan.assigned_to || '');
    setError('');
  }, [plan]);

  useEffect(() => {
    if (!plan || !organisationId) return;
    (async () => {
      const token = await getToken();
      const res = await axios.get(`${BASE_URL}/api/contractdropdown`, {
        params: { organisation_id: organisationId }, headers: { Authorization: `Bearer ${token}` },
      });
      setContracts(res.data || []);
    })().catch(console.error);
  }, [plan, organisationId]);

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const token = await getToken();
      await axios.put(`${BASE_URL}/api/maintenance-plans/${plan.id}`, {
        assigned_to: assignedTo || null,
        contract_id: contractId || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
      onClose();
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to update plan';
      setError(message);
      onError?.(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!plan} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit plan{plan?.name ? `: ${plan.name}` : ''}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <FormControl size="small" fullWidth>
          <InputLabel>Contract (optional)</InputLabel>
          <Select value={contractId} label="Contract (optional)" onChange={e => setContractId(e.target.value)}>
            <MenuItem value="">None</MenuItem>
            {contracts.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Assigned to</InputLabel>
          <Select value={assignedTo} label="Assigned to" onChange={e => setAssignedTo(e.target.value)}>
            <MenuItem value="">Unassigned</MenuItem>
            {renderAssignableMenuItems(staff, vendorCrew)}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? <CircularProgress size={16} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Schedules tab
// ─────────────────────────────────────────────────────────────────────────────
const SchedulesTab = ({ organisationId }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [historyPlanId, setHistoryPlanId] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const fetchPlans = useCallback(async () => {
    if (!organisationId) return;
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${BASE_URL}/api/maintenance-plans`, {
        params: { organisation_id: organisationId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlans(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const setStatus = async (plan, status) => {
    try {
      const token = await getToken();
      await axios.put(`${BASE_URL}/api/maintenance-plans/${plan.id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnack({ open: true, message: `Plan ${status}`, severity: 'success' });
      fetchPlans();
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || 'Failed to update plan', severity: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" size="small" startIcon={<Plus size={14} />} onClick={() => setNewOpen(true)}>
          New plan
        </Button>
      </Box>

      {loading ? <CircularProgress size={20} /> : plans.length === 0 ? (
        <Alert severity="info">No maintenance plans yet — they're created automatically when an establishment workflow with a maintenance handover completes, or manually here.</Alert>
      ) : plans.map(plan => (
        <Paper key={plan.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ cursor: 'pointer' }} onClick={() => setEditingPlan(plan)}>
              <Typography variant="subtitle2">{plan.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {plan.site_name || plan.site_id} · {plan.recurrence?.frequency} every {plan.recurrence?.interval || 1}
                {' · next due '}{fmtDate(plan.next_due)}
                {plan.assignee_name ? ` · ${plan.assignee_name}` : ' · unassigned'}
                {plan.contract_name ? ` · ${plan.contract_name}` : ''}
              </Typography>
            </Box>
            <Chip
              label={plan.status}
              size="small"
              color={plan.status === 'active' ? 'success' : plan.status === 'paused' ? 'warning' : 'default'}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Compliance (visits so far): ✅ {plan.on_time_visits} · ⏱ {plan.delayed_visits} · ❌ {plan.missed_visits}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button size="small" startIcon={<Pencil size={14} />} onClick={() => setEditingPlan(plan)}>Edit</Button>
            <Button size="small" startIcon={<History size={14} />} onClick={() => setHistoryPlanId(plan.id)}>History</Button>
            {plan.status === 'active' && (
              <Button size="small" startIcon={<Pause size={14} />} onClick={() => setStatus(plan, 'paused')}>Pause</Button>
            )}
            {plan.status === 'paused' && (
              <Button size="small" startIcon={<Play size={14} />} onClick={() => setStatus(plan, 'active')}>Resume</Button>
            )}
            {plan.status !== 'ended' && (
              <Button size="small" color="error" startIcon={<Square size={14} />} onClick={() => setStatus(plan, 'ended')}>End</Button>
            )}
          </Box>
        </Paper>
      ))}

      <NewPlanDialog open={newOpen} onClose={() => setNewOpen(false)} organisationId={organisationId} onCreated={fetchPlans} />
      <PlanHistoryDialog planId={historyPlanId} onClose={() => setHistoryPlanId(null)} />
      <EditPlanDialog
        plan={editingPlan}
        organisationId={organisationId}
        onClose={() => setEditingPlan(null)}
        onSaved={() => { setSnack({ open: true, message: 'Plan updated', severity: 'success' }); fetchPlans(); }}
        onError={(message) => setSnack({ open: true, message, severity: 'error' })}
      />
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
const Maintenance = () => {
  const { selectedOrgId } = useOrg();
  const { can } = usePermissions();
  const [tab, setTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const siteFilter = searchParams.get('site') || '';

  const canRead = can('Task Management', 'taskAssignment', 'read');
  if (!canRead) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">You do not have permission to view maintenance.</Typography></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Calendar size={22} /> Maintenance
      </Typography>
      {siteFilter && (
        <Chip
          label={`Filtered to site: ${siteFilter}`}
          onDelete={() => setSearchParams({})}
          sx={{ mb: 2 }}
        />
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab label="Work" />
        <Tab label="Schedules" />
      </Tabs>
      {tab === 0
        ? <WorkTab organisationId={selectedOrgId} siteId={siteFilter} onViewPlan={() => setTab(1)} />
        : <SchedulesTab organisationId={selectedOrgId} />}
    </Box>
  );
};

export default Maintenance;
